if (!customElements.get('kiwi-main-banner')) {
  class KiwiMainBanner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('[data-kiwi-main-banner-email]');
      this.consentInput = this.querySelector('[data-kiwi-main-banner-consent]');
      this.emailFeedback = this.querySelector('[data-kiwi-main-banner-email-feedback]');
      this.consentFeedback = this.querySelector('[data-kiwi-main-banner-consent-feedback]');
      this.consentLinks = Array.from(this.querySelectorAll('.kiwi-main-banner__consent-text a'));
      this.submitButton = this.querySelector('[data-kiwi-main-banner-submit]');
      this.successToast = this.querySelector('[data-kiwi-main-banner-success]');
      this.successToastText = this.successToast?.querySelector('.kiwi-main-banner__toast-text');
      this.floatingSuccessToast = null;

      this.emptyEmailMessage = this.dataset.emptyEmailMessage || 'Please enter your email address.';
      this.invalidEmailMessage = this.dataset.invalidEmailMessage || 'Please enter a valid email address.';
      this.consentMessage = this.dataset.consentMessage || 'Please check the consent box before subscribing.';
      this.successMessage = this.dataset.successMessage || this.successToastText?.textContent?.trim() || 'Success! You are subscribed.';
      this.currentErrorCodes = {
        email: this.emailFeedback?.textContent.trim() ? 'email-invalid' : '',
        consent: this.consentFeedback?.textContent.trim() ? 'consent' : '',
      };
      this.hasCheckedConsent = Boolean(this.consentInput?.checked);
      this.hideToastTimer = null;
      this.shouldAutoShowSuccessToast = this.dataset.startSuccessToast === 'true' || new URL(window.location.href).searchParams.get('customer_posted') === 'true';

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleEmailInput = this.handleEmailInput.bind(this);
      this.handleConsentChange = this.handleConsentChange.bind(this);
      this.handleConsentLinkClick = this.handleConsentLinkClick.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);

      this.form?.addEventListener('submit', this.handleSubmit);
      this.emailInput?.addEventListener('input', this.handleEmailInput);
      this.consentInput?.addEventListener('change', this.handleConsentChange);
      this.consentLinks.forEach((link) => link.addEventListener('click', this.handleConsentLinkClick));
      window.addEventListener('pageshow', this.handlePageShow);
      this.initializeFeedbackState();
      this.resetSubmitState();
      if (this.shouldAutoShowSuccessToast) {
        this.showSuccessToast(this.successMessage);
      }

      this.initialized = true;
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.emailInput?.removeEventListener('input', this.handleEmailInput);
      this.consentInput?.removeEventListener('change', this.handleConsentChange);
      this.consentLinks?.forEach((link) => link.removeEventListener('click', this.handleConsentLinkClick));
      window.removeEventListener('pageshow', this.handlePageShow);
      if (this.hideToastTimer) window.clearTimeout(this.hideToastTimer);
      this.removeFloatingSuccessToast();
    }

    handleSubmit(event) {
      if (!this.validate()) {
        event.preventDefault();
        this.resetSubmitState();
        return;
      }

      this.clearFeedback();
      this.submitButton?.classList.add('is-submitting');
    }

    handleEmailInput() {
      const email = (this.emailInput?.value || '').trim();
      const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      if (!email) {
        if (this.currentErrorCodes.email) {
          this.clearTargetFeedback('email');
        }
        return;
      }

      if (!emailIsValid) {
        this.setFeedback('email', this.invalidEmailMessage, 'error', 'email-invalid');
        return;
      }

      if (this.currentErrorCodes.email && emailIsValid) {
        this.clearTargetFeedback('email');
      }
    }

    handleConsentChange() {
      if (!this.consentInput) return;

      if (this.consentInput.checked) {
        this.hasCheckedConsent = true;
        this.clearTargetFeedback('consent');
        return;
      }

      if (this.hasCheckedConsent) {
        this.setFeedback('consent', this.consentMessage, 'error', 'consent');
      }
    }

    handleConsentLinkClick(event) {
      event.stopPropagation();
    }

    handlePageShow() {
      this.resetSubmitState();
    }

    validate() {
      const email = (this.emailInput?.value || '').trim();
      const consentChecked = this.consentInput?.checked;
      let firstInvalidField = null;

      this.clearFeedback();

      if (!email) {
        this.setFeedback('email', this.emptyEmailMessage, 'error', 'email-empty');
        firstInvalidField = this.emailInput;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.setFeedback('email', this.invalidEmailMessage, 'error', 'email-invalid');
        firstInvalidField = this.emailInput;
      }

      if (!consentChecked) {
        this.setFeedback('consent', this.consentMessage, 'error', 'consent');
        firstInvalidField = firstInvalidField || this.consentInput;
      }

      if (firstInvalidField) {
        firstInvalidField?.focus();
        return false;
      }

      return true;
    }

    initializeFeedbackState() {
      [this.emailFeedback, this.consentFeedback].forEach((feedback) => {
        if (!feedback) return;

        if (feedback.textContent.trim()) {
          feedback.removeAttribute('hidden');
          return;
        }

        feedback.setAttribute('hidden', 'hidden');
      });
    }

    clearFeedback() {
      this.currentErrorCodes.email = '';
      this.currentErrorCodes.consent = '';
      [this.emailFeedback, this.consentFeedback].forEach((feedback) => {
        if (!feedback) return;

        this.resetFeedbackElement(feedback);
      });
    }

    clearTargetFeedback(target) {
      const feedback = target === 'consent' ? this.consentFeedback : this.emailFeedback;
      if (!feedback) return;

      this.currentErrorCodes[target] = '';
      this.resetFeedbackElement(feedback);
    }

    resetFeedbackElement(feedback) {
      feedback.textContent = '';
      feedback.classList.remove('is-error', 'is-success');
      feedback.setAttribute('hidden', 'hidden');
    }

    setFeedback(target, message, type, errorCode = '') {
      const feedback = target === 'consent' ? this.consentFeedback : this.emailFeedback;
      if (!feedback) return;

      this.currentErrorCodes[target] = type === 'error' ? errorCode : '';
      this.resetFeedbackElement(feedback);
      feedback.textContent = message;
      feedback.removeAttribute('hidden');

      if (type === 'error') feedback.classList.add('is-error');
      if (type === 'success') feedback.classList.add('is-success');
    }

    resetSubmitState() {
      this.submitButton?.classList.remove('is-submitting');
    }

    showSuccessToast(message = this.successMessage) {
      if (!this.successToast) return;

      if (this.successToastText) {
        this.successToastText.textContent = message;
      }
      this.removeFloatingSuccessToast();
      this.successToast.setAttribute('hidden', 'hidden');
      this.floatingSuccessToast = this.successToast.cloneNode(true);
      this.floatingSuccessToast.removeAttribute('hidden');
      this.floatingSuccessToast.classList.add('kiwi-main-banner__toast--floating');
      const floatingSuccessToastText = this.floatingSuccessToast.querySelector('.kiwi-main-banner__toast-text');
      if (floatingSuccessToastText) {
        floatingSuccessToastText.textContent = message;
      }
      document.body.appendChild(this.floatingSuccessToast);

      window.requestAnimationFrame(() => {
        this.floatingSuccessToast?.classList.add('is-entered');
      });

      this.hideToastTimer = window.setTimeout(() => {
        this.floatingSuccessToast?.classList.remove('is-visible', 'is-entered');
        window.setTimeout(() => this.removeFloatingSuccessToast(), 320);
      }, 4200);
    }

    removeFloatingSuccessToast() {
      if (!this.floatingSuccessToast) return;

      this.floatingSuccessToast.remove();
      this.floatingSuccessToast = null;
    }
  }

  customElements.define('kiwi-main-banner', KiwiMainBanner);
}
